import { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';

// Types
export interface User {
  id: string;
  email: string;
  username?: string;
  email_verified: boolean;
  is_active: boolean;
  created_at: string;
  last_login_at?: string;
  two_factor_enabled: boolean;
  profile?: UserProfile;
  subscription?: UserSubscription;
}

export interface UserProfile {
  display_name?: string;
  avatar_url?: string;
  timezone: string;
  language: string;
  country?: string;
  preferences: {
    default_video_quality: string;
    default_audio_format: string;
    auto_download: boolean;
    download_history_enabled: boolean;
    email_notifications: boolean;
    theme: string;
  };
}

export interface UserSubscription {
  tier: 'free' | 'premium' | 'professional' | 'enterprise';
  expires_at?: string;
  features: Record<string, any>;
  usage_limits: Record<string, any>;
  payment_method?: string;
  billing_cycle?: string;
  is_active: boolean;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  tokens: {
    accessToken: string | null;
    refreshToken: string | null;
  };
}

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; accessToken: string; refreshToken: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_CLEAR_ERROR' }
  | { type: 'AUTH_UPDATE_USER'; payload: User }
  | { type: 'AUTH_UPDATE_PROFILE'; payload: UserProfile }
  | { type: 'TOKEN_REFRESH'; payload: { accessToken: string; user: User } };

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  tokens: {
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken'),
  },
};

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      // Store tokens in localStorage
      localStorage.setItem('accessToken', action.payload.accessToken);
      localStorage.setItem('refreshToken', action.payload.refreshToken);
      
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        tokens: {
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        },
      };

    case 'AUTH_FAILURE':
      // Clear tokens on failure
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        tokens: {
          accessToken: null,
          refreshToken: null,
        },
      };

    case 'AUTH_LOGOUT':
      // Clear tokens on logout
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        tokens: {
          accessToken: null,
          refreshToken: null,
        },
      };

    case 'AUTH_CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'AUTH_UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };

    case 'AUTH_UPDATE_PROFILE':
      return {
        ...state,
        user: state.user ? {
          ...state.user,
          profile: action.payload,
        } : null,
      };

    case 'TOKEN_REFRESH':
      // Update access token
      localStorage.setItem('accessToken', action.payload.accessToken);
      
      return {
        ...state,
        user: action.payload.user,
        tokens: {
          ...state.tokens,
          accessToken: action.payload.accessToken,
        },
      };

    default:
      return state;
  }
}

// Context
interface AuthContextType extends AuthState {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, username?: string) => Promise<void>;
  logout: () => void;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // API base URL - works with Docker Compose dev setup
  const getApiBase = () => {
    if (import.meta.env.VITE_API_URL) {
      return import.meta.env.VITE_API_URL;
    }
    
    // In Docker Compose development, use relative paths 
    // The frontend proxy will handle forwarding to backend-dev:5000
    return '/api';
  };
  
  const API_BASE = getApiBase();

  const checkAuth = async () => {
    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      return;
    }

    try {
      dispatch({ type: 'AUTH_START' });
      
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: data.user,
            accessToken: token,
            refreshToken: localStorage.getItem('refreshToken') || '',
          },
        });
      } else {
        // Token is invalid, try to refresh
        try {
          await refreshToken();
        } catch (error) {
          // Refresh failed, clear everything
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      }
    } catch (error) {
      // Clear invalid tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuth();
  }, []);

  // Helper function to make authenticated requests
  const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
    const token = state.tokens.accessToken;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    // Handle token refresh if needed
    if (response.status === 401 && token) {
      try {
        await refreshToken();
        // Retry the request with new token
        const newToken = localStorage.getItem('accessToken');
        return fetch(url, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            ...(newToken && { Authorization: `Bearer ${newToken}` }),
            ...options.headers,
          },
        });
      } catch (error) {
        // If refresh fails, logout user
        logout();
        throw new Error('Session expired. Please login again.');
      }
    }

    return response;
  };

  // Login function
  const login = useCallback(async (email: string, password: string, rememberMe = false) => {
    dispatch({ type: 'AUTH_START' });

    try {
      console.log('🔄 Attempting login to:', `${API_BASE}/auth/login`);
      
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
      });

      console.log('📡 Login response status:', response.status);

      // Handle non-JSON responses
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('❌ Failed to parse login response as JSON:', jsonError);
        throw new Error('Server error: Invalid response format. Please check if the backend is running.');
      }

      if (!response.ok) {
        console.error('❌ Login failed:', data);
        throw new Error(data.error || `Login failed (${response.status})`);
      }

      console.log('✅ Login successful:', data);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: data.user,
          accessToken: data.access_token,
          refreshToken: data.refresh_token,
        },
      });
    } catch (error) {
      console.error('❌ Login error:', error);
      
      let errorMessage = 'Login failed';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error: Cannot connect to server. Please check if the backend is running.';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      throw new Error(errorMessage);
    }
  }, [API_BASE]);

  // Register function
  const register = useCallback(async (email: string, password: string, username?: string) => {
    dispatch({ type: 'AUTH_START' });

    try {
      console.log('🔄 Attempting registration to:', `${API_BASE}/auth/register`);
      
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, username }),
      });

      console.log('📡 Registration response status:', response.status);

      // Handle non-JSON responses (like HTML error pages)
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('❌ Failed to parse response as JSON:', jsonError);
        throw new Error('Server error: Invalid response format. Please check if the backend is running.');
      }

      if (!response.ok) {
        console.error('❌ Registration failed:', data);
        throw new Error(data.error || `Registration failed (${response.status})`);
      }

      console.log('✅ Registration successful:', data);
      
      // Registration successful but user needs to verify email
      dispatch({ type: 'AUTH_CLEAR_ERROR' });
      
      // Don't auto-login, user needs to verify email first
      return data;
    } catch (error) {
      console.error('❌ Registration error:', error);
      
      let errorMessage = 'Registration failed';
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = 'Network error: Cannot connect to server. Please check if the backend is running.';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      dispatch({
        type: 'AUTH_FAILURE',
        payload: errorMessage,
      });
      throw new Error(errorMessage);
    }
  }, [API_BASE]);

  // Logout function
  const logout = useCallback(() => {
    // Call logout endpoint to invalidate server-side tokens if needed
    if (state.tokens.accessToken) {
      fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${state.tokens.accessToken}`,
        },
      }).catch(() => {
        // Ignore errors, still logout locally
      });
    }

    dispatch({ type: 'AUTH_LOGOUT' });
  }, [API_BASE, state.tokens.accessToken]);

  // Email verification
  const verifyEmail = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Email verification failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Resend verification email
  const resendVerification = async (email: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Forgot password
  const forgotPassword = async (email: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send password reset email');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (token: string, newPassword: string) => {
    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, new_password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password reset failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Change password
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Password change failed');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  // Update profile
  const updateProfile = async (profileData: Partial<UserProfile>) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE}/auth/profile`, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Profile update failed');
      }

      dispatch({ type: 'AUTH_UPDATE_PROFILE', payload: data.profile });
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Refresh token
  const refreshToken = async () => {
    const refreshToken = state.tokens.refreshToken;
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${API_BASE}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${refreshToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Token refresh failed');
      }

      dispatch({
        type: 'TOKEN_REFRESH',
        payload: {
          accessToken: data.access_token,
          user: data.user,
        },
      });
    } catch (error) {
      // If refresh fails, logout user
      dispatch({ type: 'AUTH_LOGOUT' });
      throw error;
    }
  };

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'AUTH_CLEAR_ERROR' });
  }, []);

  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    changePassword,
    updateProfile,
    refreshToken,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext; 